import { makeAutoObservable } from 'mobx';
import updateElectionData from './fn/updateElectionData';
import updateElectionAttributeData from './fn/updateElectionAttributeData';
import getSelectedElectionById from './fn/getSelectedElectionById';
import getSelectedElectionUpdateAttributes from './fn/getSelectedElectionUpdateAttributes';
import getSelectedElectionAttribute from './fn/getSelectedElectionAttribute';
import getSelectedRegionAttribute from './fn/getSelectedRegionAttribute';
import getSelectedRegionUpdateAttributes from './fn/getSelectedRegionUpdateAttributes';
import updateRegionAttributeData from './fn/updateRegionAttributeData';
import updateRegionSettingsData from './fn/updateRegionSettingsData';
import { Election, ElectionAttribute } from '~/definitions/election';
import { Region, RegionAttribute } from '~/definitions/region';
import setRegionData from './fn/setRegionData';
import handleChangeRegionAttribute from './fn/handleChangeRegionAttribute';
import setElectionData from './fn/setElectionData';
import handleChangeElectionAttribute from './fn/handleChangeElectionAttribute';
import handleUpdateElection from './fn/handleUpdateElection';
import fetchElectionData from './fn/fetchElectionData';
import getElectionById from './fn/getElectionById';
import getRegionById from './fn/getRegionById';
import { getIsLoading } from './fn/getIsLoading';
import { showRegionErrorModal } from './fn/showRegionErrorModal';
import getSelectedElectionYear from './fn/getSelectedElectionYear';

export default class GeneralSettingsStore {
    isSettingsDisabled: boolean = false;

    // Election setting
    latestElectionId: string = '';
    preSelectElectionId: string = '';
    selectedElectionId: string = '';
    slagDescription: string = '';
    slagHowToVote: string = '';

    companyElectionData: ElectionAttribute[] = [];
    electionList: Election[] = [];
    selectedElectionData: Election = {};

    // Region setting
    regionName: string = '';
    regionDescription: string = '';
    regionExtraVoteLabel: string = '';
    regionExtraVoteQuantity: string = '';
    regionNoVoteDescription: string = '';
    regionNameError: boolean = false;
    regionExtraVoteQuantityError: boolean = false;

    regionSettingsData: RegionAttribute[] = [];
    selectedRegionData: Region = {};

    isLoadingElectionData: boolean = false;
    isLoadingElectionList: boolean = false;
    isUpdatingElectionData: boolean = false;
    isLoadingRegionData: boolean = false;
    isUpdatingRegionData: boolean = false;

    constructor() {
        this.resetToDefault();
        makeAutoObservable(this);
    }

    resetToDefault() {
        this.isSettingsDisabled = false;

        // Election
        this.latestElectionId = '';
        this.preSelectElectionId = '';
        this.selectedElectionId = '';
        this.slagDescription = '';
        this.slagHowToVote = '';

        this.companyElectionData = [];
        this.electionList = [];
        this.selectedElectionData = {};

        // Region
        this.regionName = '';
        this.regionDescription = '';
        this.regionExtraVoteLabel = '';
        this.regionExtraVoteQuantity = '';
        this.regionNoVoteDescription = '';
        this.regionNameError = false;
        this.regionExtraVoteQuantityError = false;

        this.regionSettingsData = [];
        this.selectedRegionData = {};

        this.isLoadingElectionData = false;
        this.isLoadingElectionList = false;
        this.isUpdatingElectionData = false;
    }

    setIsSettingsDisabled = (isDisable: boolean) => {
        this.isSettingsDisabled = isDisable;
    };

    setLatestElectionId = (id: string) => {
        this.latestElectionId = id;
    };

    setPreSelectElectionId = (id: string) => {
        this.preSelectElectionId = id;
    };

    setSelectedElectionId = (id: string) => {
        this.selectedElectionId = id;
    };

    setSlagDescription = (value: string) => {
        this.slagDescription = value;
    };

    setSlagHowToVote = (value: string) => {
        this.slagHowToVote = value;
    };

    setRegionName = (value: string) => {
        this.regionName = value;
    };

    setRegionDescription = (value: string) => {
        this.regionDescription = value;
    };

    setRegionExtraVoteLabel = (value: string) => {
        this.regionExtraVoteLabel = value;
    };

    setRegionExtraVoteQuantity = (value: string) => {
        this.regionExtraVoteQuantity = value;
    };

    setRegionNoVoteDescription = (value: string) => {
        this.regionNoVoteDescription = value;
    };

    setRegionNameError = (isError: boolean) => {
        this.regionNameError = isError;
    };

    setRegionExtraVoteQuantityError = (isError: boolean) => {
        this.regionExtraVoteQuantityError = isError;
    };

    setRegionSettingsData = (data: RegionAttribute[]) => {
        this.regionSettingsData = data;
    };

    setSelectedRegionData = (data: Region) => {
        this.selectedRegionData = data;
    };

    setCompanyElectionData = (data: ElectionAttribute[]) => {
        this.companyElectionData = data;
    };

    setElectionList = (data: Election[]) => {
        this.electionList = data;
    };

    setSelectedElectionData = (data: Election) => {
        this.selectedElectionData = data;
    };

    setIsLoadingElectionData = (isLoading: boolean) => {
        this.isLoadingElectionData = isLoading;
    };

    setIsLoadingElectionList = (isLoading: boolean) => {
        this.isLoadingElectionList = isLoading;
    };

    setIsUpdatingElectionData = (isLoading: boolean) => {
        this.isUpdatingElectionData = isLoading;
    };

    setIsLoadingRegionData = (isLoading: boolean) => {
        this.isLoadingRegionData = isLoading;
    };

    setIsUpdatingRegionData = (isLoading: boolean) => {
        this.isUpdatingRegionData = isLoading;
    };

    fetchElectionData = fetchElectionData;
    getElectionById = getElectionById;
    getRegionById = getRegionById;
    getIsLoading = getIsLoading;
    getSelectedElectionAttribute = getSelectedElectionAttribute;
    getSelectedElectionById = getSelectedElectionById;
    getSelectedElectionUpdateAttributes = getSelectedElectionUpdateAttributes;
    getSelectedRegionAttribute = getSelectedRegionAttribute;
    getSelectedRegionUpdateAttributes = getSelectedRegionUpdateAttributes;
    handleChangeElectionAttribute = handleChangeElectionAttribute;
    handleChangeRegionAttribute = handleChangeRegionAttribute;
    handleUpdateElection = handleUpdateElection;
    updateElectionData = updateElectionData;
    updateElectionAttributeData = updateElectionAttributeData;
    updateRegionAttributeData = updateRegionAttributeData;
    updateRegionSettingsData = updateRegionSettingsData;
    setElectionData = setElectionData;
    setRegionData = setRegionData;
    showRegionErrorModal = showRegionErrorModal;
    getSelectedElectionYear = getSelectedElectionYear;
}
